<!-- Beta Banner -->
<!-- <div class="beta-banner" *ngIf="isBeta">
  <span class="beta-dot"></span>
  You are viewing this Privacy Policy on <strong>beta.angularize.dev</strong>.
  The canonical version lives at
  <a routerLink="/privacy-policy" target="_blank" rel="noopener noreferrer"
    >angularize.dev/privacy-policy</a
  >.
</div> -->

<!-- Page wrapper — no header/footer, those come from global layout -->
<div class="pp-wrap">
  <!-- HERO -->
  <header class="pp-hero" role="banner">
    <div class="pp-hero-glow" aria-hidden="true"></div>
    <div class="pp-eyebrow"><span aria-hidden="true">🔒</span> Privacy</div>
    <h1 class="pp-title">Privacy <span class="text-accent">Policy</span></h1>
    <p class="pp-sub">
      This policy explains how
      <strong>Pythrust Technologies OPC Private Limited</strong>
      collects, uses, stores, and protects your personal data when you use
      <strong>{{ platformLabel }}</strong> and associated services.
    </p>
    <div class="pp-meta">
      <div class="meta-pill"><strong>Effective:</strong> {{ lastUpdated }}</div>
      <div class="meta-pill">
        <strong>Owner:</strong> Pythrust Technologies OPC Pvt. Ltd.
      </div>
      <div class="meta-pill">
        <strong>Jurisdiction:</strong> Gurugram, Haryana, India
      </div>
      <div class="meta-pill">
        <strong>Compliance:</strong> DPDPA 2023 · IT Act 2000 · GDPR-aligned
      </div>
    </div>
  </header>

  <!-- BODY -->
  <div class="pp-layout">
    <!-- SIDEBAR TOC -->
    <aside class="toc" aria-label="Table of Contents">
      <p class="toc-label">Contents</p>
      <ul class="toc-list">
        <li *ngFor="let s of tocSections">
          <a
            [class.active]="s.active"
            (click)="scrollTo(s.id)"
            (keydown.enter)="scrollTo(s.id)"
            tabindex="0"
            [attr.aria-current]="s.active ? 'location' : null"
            >{{ s.label }}</a
          >
        </li>
      </ul>
    </aside>

    <!-- MAIN CONTENT -->
    <main class="pp-content" role="main">
      <!-- ── P1: INTRODUCTION ── -->
      <section class="sec" id="p1">
        <span class="sec-num">Section 01</span>
        <h2 class="sec-heading">Introduction</h2>
        <p>
          Pythrust Technologies OPC Private Limited ("<strong>Company</strong>",
          "<strong>we</strong>", "<strong>us</strong>", or
          "<strong>our</strong>") is committed to protecting and respecting your
          privacy. This Privacy Policy ("<strong>Policy</strong>") describes how
          we collect, use, disclose, and safeguard personal data when you access
          or use:
        </p>
        <ul class="doc-list">
          <li>
            The primary web application at <strong>angularize.dev</strong>;
          </li>
          <li>The beta environment at <strong>beta.angularize.dev</strong>;</li>
          <li>
            Any APIs, documentation, or related services (collectively, the
            "<strong>Platform</strong>").
          </li>
        </ul>
        <p>
          By accessing the Platform, you acknowledge that you have read,
          understood, and agree to the practices described in this Policy. If
          you do not agree, please discontinue use of the Platform immediately.
        </p>
        <div class="callout callout-info">
          <strong>ℹ Regulatory Framework</strong>
          This Policy is designed to comply with the
          <strong>Digital Personal Data Protection Act, 2023 (India)</strong>,
          the <strong>Information Technology Act, 2000</strong> and its Rules,
          and is aligned with <strong>GDPR</strong> principles for users in the
          European Economic Area. Where Indian law and GDPR diverge, Indian law
          governs for Indian residents.
        </div>
      </section>

      <!-- ── P2: WHO WE ARE ── -->
      <section class="sec" id="p2">
        <span class="sec-num">Section 02</span>
        <h2 class="sec-heading">Who We Are — Data Fiduciary</h2>
        <p>
          Under the Digital Personal Data Protection Act, 2023
          ("<strong>DPDPA</strong>"), Pythrust Technologies OPC Private Limited
          is the <strong>Data Fiduciary</strong>
          responsible for determining the purposes and means of processing your
          personal data.
        </p>
        <div class="contact-card">
          <div class="contact-item">
            <span class="lbl">Company</span>Pythrust Technologies OPC Private
            Limited
          </div>
          <div class="contact-item">
            <span class="lbl">Product</span>Angularize.dev
          </div>
          <div class="contact-item">
            <span class="lbl">Email</span
            ><a href="mailto:angularize.dev@gmail.com"
              >angularize.dev&#64;gmail.com</a
            >
          </div>
          <div class="contact-item">
            <span class="lbl">Address</span>
            WeWork, 5th Floor, DLF Two Horizon Centre,<br />
            Harizan Colony, DLF Phase 5, Sector 43,<br />
            Gurugram, Haryana 122002, India
          </div>
        </div>
      </section>

      <!-- ── P3: DATA WE COLLECT ── -->
      <section class="sec" id="p3">
        <span class="sec-num">Section 03</span>
        <h2 class="sec-heading">Data We Collect</h2>
        <p>We collect the following categories of personal data:</p>

        <div class="data-table">
          <div class="data-table-head">
            <div>Category</div>
            <div>Examples</div>
            <div>Purpose</div>
          </div>
          <div class="data-row" *ngFor="let d of dataTable">
            <div class="data-cell cat">{{ d.category }}</div>
            <div class="data-cell">{{ d.examples }}</div>
            <div class="data-cell">{{ d.purpose }}</div>
          </div>
        </div>

        <h3 class="sub-heading">3.1 Data You Provide Directly</h3>
        <ul class="doc-list">
          <li>Registration information when creating an Account;</li>
          <li>
            Content, code, and project files you submit or generate on the
            Platform;
          </li>
          <li>Communications you send us via email or support channels;</li>
          <li>Feedback, bug reports, or survey responses.</li>
        </ul>

        <h3 class="sub-heading">3.2 Data Collected Automatically</h3>
        <ul class="doc-list">
          <li>
            Log data: IP address, browser type, OS, referring URLs, pages
            visited, timestamps;
          </li>
          <li>
            Device identifiers and session tokens (via Firebase Authentication);
          </li>
          <li>
            Usage patterns, feature interactions, and performance metrics;
          </li>
          <li>Cookies and similar tracking technologies (see Section 9).</li>
        </ul>

        <h3 class="sub-heading">3.3 Data from Third Parties</h3>
        <ul class="doc-list">
          <li>
            OAuth profile data (name, email, profile picture) when you sign in
            with Google or GitHub via Firebase Authentication;
          </li>
          <li>
            Payment confirmation data from our payment processor (we never
            receive raw card data).
          </li>
        </ul>

        <h3 class="sub-heading">3.4 Sensitive Data</h3>
        <p>
          We do not intentionally collect sensitive personal data (e.g.
          biometric data, health data, religious beliefs, political opinions)
          through the Platform. Please do not submit such data.
        </p>
      </section>

      <!-- ── P4: HOW WE USE DATA ── -->
      <section class="sec" id="p4">
        <span class="sec-num">Section 04</span>
        <h2 class="sec-heading">How We Use Your Data</h2>
        <p>We use personal data only for legitimate, specified purposes:</p>
        <ul class="doc-list">
          <li>
            <strong>Service Delivery:</strong> Providing, operating, and
            maintaining the Platform and all its features;
          </li>
          <li>
            <strong>Account Management:</strong> Creating and managing your
            Account, authentication, and subscription;
          </li>
          <li>
            <strong>Security:</strong> Detecting, preventing, and investigating
            fraud, abuse, unauthorised access, and cyber attacks;
          </li>
          <li>
            <strong>Communications:</strong> Sending transactional emails
            (account activation, password reset, billing receipts), security
            alerts, and product updates;
          </li>
          <li>
            <strong>Support:</strong> Responding to your queries and resolving
            issues;
          </li>
          <li>
            <strong>Analytics:</strong> Understanding how the Platform is used
            to improve features and user experience — using aggregated,
            anonymised data wherever possible;
          </li>
          <li>
            <strong>AI Improvement:</strong> Improving code-generation accuracy
            using anonymised, aggregated usage patterns — your proprietary code
            is never used to train general models without explicit consent;
          </li>
          <li>
            <strong>Legal Compliance:</strong> Meeting our obligations under
            applicable law, responding to lawful government requests, and
            protecting our legal rights;
          </li>
          <li>
            <strong>Business Operations:</strong> Billing, subscription
            management, and fraud prevention.
          </li>
        </ul>
        <div class="callout callout-neutral">
          <strong>📌 No Selling of Data</strong>
          We do not sell, rent, or trade your personal data to advertisers or
          third-party marketers under any circumstances.
        </div>
      </section>

      <!-- ── P5: LEGAL BASIS ── -->
      <section class="sec" id="p5">
        <span class="sec-num">Section 05</span>
        <h2 class="sec-heading">Legal Basis for Processing</h2>
        <p>
          We process your personal data on the following legal bases under the
          DPDPA 2023 and, where applicable, GDPR:
        </p>
        <ul class="doc-list">
          <li>
            <strong>Consent (DPDPA S.6 / GDPR Art. 6(1)(a)):</strong>
            Where you have given clear, informed consent — e.g. optional
            analytics, marketing communications, or AI training participation.
            You may withdraw consent at any time.
          </li>
          <li>
            <strong
              >Contract Performance (DPDPA S.4 / GDPR Art. 6(1)(b)):</strong
            >
            Processing necessary to provide the Platform you signed up for,
            including authentication, code generation, and billing.
          </li>
          <li>
            <strong>Legitimate Interests (GDPR Art. 6(1)(f)):</strong>
            For EEA users — security monitoring, fraud prevention, platform
            analytics, and improving our AI models using anonymised data,
            balanced against your privacy interests.
          </li>
          <li>
            <strong>Legal Obligation (DPDPA S.4 / GDPR Art. 6(1)(c)):</strong>
            Where processing is required to comply with applicable law,
            regulatory requirements, or court orders.
          </li>
        </ul>
      </section>

      <!-- ── P6: DATA SHARING ── -->
      <section class="sec" id="p6">
        <span class="sec-num">Section 06</span>
        <h2 class="sec-heading">Data Sharing &amp; Disclosure</h2>
        <p>
          We do not share your personal data with third parties except as
          described below:
        </p>

        <h3 class="sub-heading">6.1 Service Providers (Data Processors)</h3>
        <p>
          We engage trusted third-party service providers who process data
          strictly on our behalf and under our instructions, bound by data
          processing agreements:
        </p>
        <ul class="doc-list">
          <li>
            <strong>Google Cloud Platform (GCP):</strong> Infrastructure,
            compute, storage, and database services;
          </li>
          <li>
            <strong>Firebase (Google LLC):</strong> User authentication,
            real-time database, and hosting;
          </li>
          <li>
            <strong>Payment Processors:</strong> Billing and subscription
            management — they receive payment data under their own PCI-DSS
            compliant terms;
          </li>
          <li>
            <strong>Email Service Providers:</strong> Transactional email
            delivery;
          </li>
          <li>
            <strong>Analytics Providers:</strong> Aggregated, anonymised usage
            analytics.
          </li>
        </ul>

        <h3 class="sub-heading">6.2 Legal Requirements</h3>
        <p>
          We may disclose personal data when required by law, including in
          response to:
        </p>
        <ul class="doc-list">
          <li>
            Court orders, judicial decrees, or subpoenas from Indian or other
            competent courts;
          </li>
          <li>
            Requests from CERT-In (Indian Computer Emergency Response Team) in
            connection with cyber security incidents;
          </li>
          <li>
            Requests from law enforcement agencies under the IT Act, 2000, CrPC,
            or other applicable legislation;
          </li>
          <li>
            Government and regulatory body requests backed by legal authority.
          </li>
        </ul>

        <h3 class="sub-heading">6.3 Business Transfers</h3>
        <p>
          In the event of a merger, acquisition, or sale of assets, your
          personal data may be transferred to the acquiring entity. We will
          provide reasonable notice before any such transfer and ensure the
          successor is bound by equivalent privacy obligations.
        </p>

        <h3 class="sub-heading">6.4 Protection of Rights</h3>
        <p>
          We may disclose data where we reasonably believe it is necessary to:
          enforce these Terms; prevent fraud or abuse; protect the safety of
          users or the public; or defend legal claims against the Company.
        </p>

        <div class="callout callout-warn">
          <strong>⚠ No Unauthorised Sharing</strong>
          Outside the categories above, we never share your personal data
          without your explicit prior consent.
        </div>
      </section>

      <!-- ── P7: THIRD-PARTY SERVICES ── -->
      <section class="sec" id="p7">
        <span class="sec-num">Section 07</span>
        <h2 class="sec-heading">Third-Party Services &amp; Links</h2>
        <p>
          The Platform may contain links to third-party websites or integrate
          third-party tools. This Policy does not govern third-party data
          practices. We recommend reviewing each third party's privacy policy
          before providing them with personal data.
        </p>
        <p>
          Key third parties whose services are integrated into the Platform:
        </p>
        <ul class="doc-list">
          <li>
            <strong>Google LLC</strong> — Firebase Auth, GCP infrastructure.
            Governed by the
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              >Google Privacy Policy</a
            >;
          </li>
          <li>
            <strong>GitHub Inc.</strong> — Optional OAuth sign-in. Governed by
            <a
              href="https://docs.github.com/en/site-policy/privacy-policies/github-general-privacy-statement"
              target="_blank"
              rel="noopener noreferrer"
              >GitHub Privacy Statement</a
            >;
          </li>
          <li>
            <strong>Payment Gateways</strong> — Subject to their own PCI-DSS
            compliant privacy policies.
          </li>
        </ul>
        <p>
          We are not responsible for the privacy practices of these third
          parties.
        </p>
      </section>

      <!-- ── P8: GCP & FIREBASE ── -->
      <section class="sec" id="p8">
        <span class="sec-num">Section 08</span>
        <h2 class="sec-heading">Google Cloud Platform &amp; Firebase</h2>
        <div class="callout callout-info">
          <strong>ℹ Infrastructure Disclosure</strong>
          Our Platform is built on Google Cloud Platform (GCP) and uses Firebase
          products. This section explains what that means for your data.
        </div>

        <h3 class="sub-heading">8.1 Google Cloud Platform</h3>
        <p>
          We use GCP for our core infrastructure including compute (Cloud Run /
          GCE), Cloud Storage, Cloud SQL / Firestore databases, and related
          managed services. Google processes data on our behalf as a Data
          Processor under a
          <a
            href="https://cloud.google.com/terms/data-processing-addendum"
            target="_blank"
            rel="noopener noreferrer"
            >Google Cloud Data Processing Addendum</a
          >.
        </p>
        <ul class="doc-list">
          <li>
            GCP is certified under ISO 27001, SOC 2 Type II, SOC 3, and is
            GDPR-compliant;
          </li>
          <li>
            Data stored on GCP may be processed in Google's data centres
            globally, subject to Google's data residency commitments;
          </li>
          <li>
            Google does not sell GCP customer data or use it to serve ads;
          </li>
          <li>
            All data in GCP is encrypted at rest and in transit by default.
          </li>
        </ul>

        <h3 class="sub-heading">8.2 Firebase Authentication</h3>
        <p>
          We use Firebase Authentication (a Google service) to manage user
          identity. Firebase Auth processes:
        </p>
        <ul class="doc-list">
          <li>Email address and password hash (for email/password sign-in);</li>
          <li>
            OAuth tokens and basic profile data (name, email, profile photo) for
            Google / GitHub sign-in;
          </li>
          <li>
            Session tokens and refresh tokens used to maintain authenticated
            sessions.
          </li>
        </ul>
        <p>
          Firebase Authentication is governed by the
          <a
            href="https://firebase.google.com/support/privacy"
            target="_blank"
            rel="noopener noreferrer"
            >Firebase Privacy &amp; Security documentation</a
          >
          and Google's overall data processing terms. Firebase does not use
          authentication data for advertising purposes.
        </p>

        <h3 class="sub-heading">8.3 Firebase Other Services</h3>
        <p>
          We may also use: <strong>Firestore</strong> (NoSQL database for
          real-time data), <strong>Firebase Hosting</strong> (static asset
          delivery), and <strong>Firebase Analytics</strong> (aggregated usage
          insights). Each product is governed by Google's data processing terms.
        </p>

        <h3 class="sub-heading">8.4 Our Obligations as GCP Customer</h3>
        <p>
          As a GCP customer, we are responsible for configuring these services
          appropriately, including setting appropriate access controls,
          encrypting sensitive fields, and ensuring our use of GCP complies with
          applicable privacy law. We maintain a Google Cloud Data Processing
          Addendum (Cloud DPA) covering all GCP and Firebase processing.
        </p>

        <h3 class="sub-heading">
          8.5 Company Liability Limitation for GCP/Firebase
        </h3>
        <div class="callout callout-warn">
          <strong>⚠ Important Notice</strong>
          While we configure and maintain GCP and Firebase infrastructure with
          commercially reasonable security practices, Pythrust Technologies OPC
          Private Limited is not liable for data incidents arising solely from
          security vulnerabilities, outages, or data breaches within Google's
          infrastructure that are outside our reasonable control. In such
          events, we will notify affected users and cooperate with Google's
          incident response procedures in accordance with applicable law.
        </div>
      </section>

      <!-- ── P9: COOKIES ── -->
      <section class="sec" id="p9">
        <span class="sec-num">Section 09</span>
        <h2 class="sec-heading">Cookies &amp; Tracking Technologies</h2>
        <h3 class="sub-heading">9.1 What We Use</h3>
        <p>We use the following types of cookies and similar technologies:</p>
        <ul class="doc-list">
          <li>
            <strong>Strictly Necessary Cookies:</strong> Session tokens, CSRF
            tokens, and authentication cookies required for the Platform to
            function. These cannot be disabled.
          </li>
          <li>
            <strong>Functional Cookies:</strong> Remembering your preferences
            (theme, language, last-visited project).
          </li>
          <li>
            <strong>Analytics Cookies:</strong> Aggregate usage data to
            understand how the Platform is used. Used with anonymisation where
            possible.
          </li>
          <li>
            <strong>Firebase SDK Cookies:</strong> Firebase Authentication sets
            cookies/local storage tokens for session management. These are
            essential to the login system.
          </li>
        </ul>
        <h3 class="sub-heading">9.2 Your Cookie Choices</h3>
        <p>
          You can control non-essential cookies through our cookie settings
          panel in the Platform. You may also configure your browser to block
          all cookies, but this may impair your ability to use the Platform.
          Blocking strictly necessary cookies will prevent you from logging in.
        </p>
        <h3 class="sub-heading">9.3 Do Not Track</h3>
        <p>
          Our Platform does not currently respond to browser "Do Not Track"
          signals, but we minimise tracking to what is necessary for Platform
          operation and improvement.
        </p>
      </section>

      <!-- ── P10: DATA RETENTION ── -->
      <section class="sec" id="p10">
        <span class="sec-num">Section 10</span>
        <h2 class="sec-heading">Data Retention</h2>
        <p>
          We retain personal data only for as long as necessary for the purposes
          for which it was collected:
        </p>
        <ul class="doc-list">
          <li>
            <strong>Account Data:</strong> Retained for the duration of your
            active Account plus 90 days post-deletion (to allow account
            recovery), then permanently deleted;
          </li>
          <li>
            <strong>User Content &amp; Code:</strong> Deleted within 90 days of
            Account deletion request;
          </li>
          <li>
            <strong>Usage &amp; Log Data:</strong> Retained for up to 12 months
            for security and analytics, then anonymised or deleted;
          </li>
          <li>
            <strong>Payment Records:</strong> Retained for 7 years as required
            by Indian accounting and tax law;
          </li>
          <li>
            <strong>Support Communications:</strong> Retained for 2 years from
            last interaction;
          </li>
          <li>
            <strong>Legal Hold Data:</strong> Retained for the duration of any
            active dispute, legal proceeding, or regulatory investigation, then
            deleted when no longer required.
          </li>
        </ul>
        <p>
          After the applicable retention period, data is securely deleted or
          anonymised such that it can no longer be attributed to an identifiable
          individual.
        </p>
      </section>

      <!-- ── P11: DATA SECURITY ── -->
      <section class="sec" id="p11">
        <span class="sec-num">Section 11</span>
        <h2 class="sec-heading">Data Security</h2>
        <p>
          We implement a comprehensive set of technical and organisational
          measures to protect your personal data:
        </p>

        <h3 class="sub-heading">11.1 Technical Measures</h3>
        <ul class="doc-list">
          <li>TLS 1.2+ encryption for all data in transit;</li>
          <li>
            AES-256 encryption at rest for databases and storage (via GCP
            default encryption);
          </li>
          <li>
            Firebase Authentication with industry-standard password hashing
            (bcrypt/scrypt);
          </li>
          <li>
            Role-Based Access Control (RBAC) limiting internal data access on a
            need-to-know basis;
          </li>
          <li>
            Web Application Firewall (WAF) and DDoS mitigation via GCP's
            security infrastructure;
          </li>
          <li>
            API rate limiting, input validation, and output encoding to prevent
            injection attacks;
          </li>
          <li>
            Automated vulnerability scanning and dependency auditing in our
            CI/CD pipeline;
          </li>
          <li>
            Multi-factor authentication (MFA) required for all administrative
            access to production systems.
          </li>
        </ul>

        <h3 class="sub-heading">11.2 Organisational Measures</h3>
        <ul class="doc-list">
          <li>
            Security training for all team members with access to personal data;
          </li>
          <li>
            Strict need-to-know internal access controls with access logging;
          </li>
          <li>
            Vendor due diligence for all third-party processors, including GCP
            and Firebase;
          </li>
          <li>
            Regular internal security reviews and periodic third-party
            penetration tests;
          </li>
          <li>
            A documented Incident Response Plan aligned with CERT-In reporting
            obligations.
          </li>
        </ul>

        <h3 class="sub-heading">11.3 Breach Notification</h3>
        <p>
          In the event of a personal data breach that poses a risk to your
          rights and interests, we will:
        </p>
        <ul class="doc-list">
          <li>
            Notify the Data Protection Board of India (once constituted) within
            the timeframe prescribed under the DPDPA 2023;
          </li>
          <li>
            Notify affected users without undue delay, with details of the
            breach, data affected, and remediation steps;
          </li>
          <li>
            Document the breach and our response in our internal breach
            register.
          </li>
        </ul>

        <div class="callout callout-neutral">
          <strong>📌 No Absolute Guarantee</strong>
          Despite best-in-class measures, no system is entirely immune to
          security incidents. We cannot guarantee absolute security of your data
          transmitted over the internet.
        </div>
      </section>

      <!-- ── P12: INTERNATIONAL TRANSFERS ── -->
      <section class="sec" id="p12">
        <span class="sec-num">Section 12</span>
        <h2 class="sec-heading">International Data Transfers</h2>
        <p>
          As we use Google Cloud Platform, your data may be processed in Google
          data centres outside India, including in the United States and other
          countries where Google operates. These transfers occur because GCP's
          infrastructure is globally distributed.
        </p>
        <p>We ensure such transfers are lawful by relying on:</p>
        <ul class="doc-list">
          <li>
            Google Cloud's <strong>Data Processing Addendum</strong>, which
            incorporates Standard Contractual Clauses (SCCs) for EEA transfers;
          </li>
          <li>
            GCP's <strong>ISO 27001</strong> and
            <strong>SOC 2 Type II</strong> certifications providing adequate
            protection equivalence;
          </li>
          <li>
            Google's commitments under the
            <strong>EU-US Data Privacy Framework</strong> where applicable.
          </li>
        </ul>
        <p>
          For Indian users, cross-border transfers comply with restrictions to
          be notified under the DPDPA 2023 by the Indian Government. We will
          update this Policy as those regulations are finalised and notified.
        </p>
      </section>

      <!-- ── P13: CHILDREN'S PRIVACY ── -->
      <section class="sec" id="p13">
        <span class="sec-num">Section 13</span>
        <h2 class="sec-heading">Children's Privacy</h2>
        <p>
          The Platform is not directed to individuals under the age of
          <strong>18</strong>. We do not knowingly collect personal data from
          minors. If you believe a minor has provided us with personal data
          without appropriate consent, please contact us at
          <a href="mailto:angularize.dev@gmail.com"
            >angularize.dev&#64;gmail.com</a
          >
          and we will promptly delete that data.
        </p>
        <p>
          Under the DPDPA 2023, we will implement additional age-verification
          mechanisms as and when required by law or as directed by the Data
          Protection Board of India.
        </p>
      </section>

      <!-- ── P14: YOUR RIGHTS ── -->
      <section class="sec" id="p14">
        <span class="sec-num">Section 14</span>
        <h2 class="sec-heading">Your Data Rights</h2>
        <p>
          Depending on your jurisdiction, you have the following rights with
          respect to your personal data. Indian residents have rights under the
          DPDPA 2023; EEA residents have rights under GDPR.
        </p>
        <ul class="doc-list">
          <li>
            <strong>Right to Access (DPDPA S.11 / GDPR Art.15):</strong>
            Request a summary of personal data we hold about you and how it is
            being used.
          </li>
          <li>
            <strong>Right to Correction (DPDPA S.12 / GDPR Art.16):</strong>
            Request correction of inaccurate or incomplete personal data.
          </li>
          <li>
            <strong
              >Right to Erasure / Right to be Forgotten (DPDPA S.12 / GDPR
              Art.17):</strong
            >
            Request deletion of your personal data where it is no longer
            necessary or where you withdraw consent, subject to legal retention
            obligations.
          </li>
          <li>
            <strong>Right to Grievance Redressal (DPDPA S.13):</strong>
            File a complaint with our Grievance Officer (see Section 18) if you
            believe your data rights have been violated.
          </li>
          <li>
            <strong>Right to Data Portability (GDPR Art.20):</strong>
            For EEA users — receive your personal data in a structured, commonly
            used, machine-readable format.
          </li>
          <li>
            <strong>Right to Restrict Processing (GDPR Art.18):</strong>
            For EEA users — request restriction of processing in certain
            circumstances.
          </li>
          <li>
            <strong>Right to Object (GDPR Art.21):</strong>
            For EEA users — object to processing based on legitimate interests.
          </li>
          <li>
            <strong>Right to Withdraw Consent:</strong>
            Withdraw consent at any time without affecting the lawfulness of
            prior processing.
          </li>
          <li>
            <strong>Right to Nominate (DPDPA S.14):</strong>
            Nominate another individual to exercise your rights in the event of
            your death or incapacity.
          </li>
        </ul>
        <p>
          To exercise any right, submit a request to
          <a href="mailto:angularize.dev@gmail.com"
            >angularize.dev&#64;gmail.com</a
          >
          with your registered email address and the right you wish to exercise.
          We will respond within <strong>30 days</strong>. We may request
          verification of your identity before processing your request.
        </p>
        <p>
          If you are not satisfied with our response, you have the right to
          lodge a complaint with:
        </p>
        <ul class="doc-list">
          <li>
            <strong>India:</strong> The Data Protection Board of India (once
            constituted under DPDPA 2023);
          </li>
          <li><strong>EEA:</strong> Your local Supervisory Authority.</li>
        </ul>
      </section>

      <!-- ── P15: BETA PLATFORM ── -->
      <section class="sec" id="p15">
        <span class="sec-num">Section 15</span>
        <h2 class="sec-heading">Beta Platform — Privacy Notice</h2>
        <div class="callout callout-warn">
          <strong>⚠ Beta Warning</strong>
          Special privacy considerations apply to
          <strong>beta.angularize.dev</strong>.
        </div>
        <ul class="doc-list">
          <li>
            Data stored in the beta environment is subject to the same
            processing as the primary platform but may be held in separate beta
            infrastructure with less stringent backup guarantees;
          </li>
          <li>
            Beta user data may be deleted, reset, or migrated at any time
            without advance notice;
          </li>
          <li>
            Do not submit sensitive, confidential, or production-critical data
            to the beta environment;
          </li>
          <li>
            Bug reports and feedback submitted through the beta platform may be
            reviewed by our engineering team in identifiable form to reproduce
            issues;
          </li>
          <li>
            Privacy rights (Section 14) apply equally to data collected through
            the beta platform.
          </li>
        </ul>
      </section>

      <!-- ── P16: AI & GENERATED CONTENT ── -->
      <section class="sec" id="p16">
        <span class="sec-num">Section 16</span>
        <h2 class="sec-heading">AI Features &amp; Generated Content Privacy</h2>
        <h3 class="sub-heading">16.1 What AI Processes</h3>
        <p>
          When you use AI-powered code generation features, the Platform
          processes your Input (prompts, requirements, code snippets) through
          our AI systems running on GCP infrastructure to produce Generated
          Output (code, components, scaffolding).
        </p>
        <h3 class="sub-heading">16.2 No General AI Training Without Consent</h3>
        <p>
          Your proprietary Input and User Content are <strong>not</strong> used
          to train our general AI models without your explicit, opt-in consent.
          We use aggregated, anonymised usage patterns (not identifiable code or
          prompts) to improve the Platform's capabilities.
        </p>
        <h3 class="sub-heading">16.3 Input Retention</h3>
        <p>
          Prompts and inputs submitted to the AI system may be temporarily
          retained for up to
          <strong>30 days</strong> for debugging, quality assurance, and safety
          monitoring, after which they are deleted or anonymised.
        </p>
        <h3 class="sub-heading">16.4 Third-Party AI Models</h3>
        <p>
          If we use third-party large language model APIs as part of our stack,
          those providers process your Input under their own terms and privacy
          policies. We will maintain data processing agreements with any such
          providers and disclose material changes to our AI infrastructure in
          updates to this Policy.
        </p>
        <h3 class="sub-heading">16.5 No Sensitive Data in AI Prompts</h3>
        <div class="callout callout-warn">
          <strong>⚠ Caution</strong>
          Do not submit personal data, credentials, API keys, health data,
          financial data, or other sensitive information in prompts or code
          uploaded to the AI system.
        </div>
      </section>

      <!-- ── P17: CHANGES ── -->
      <section class="sec" id="p17">
        <span class="sec-num">Section 17</span>
        <h2 class="sec-heading">Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy periodically to reflect changes in
          our data practices, legal requirements, or Platform features. For
          material changes, we will:
        </p>
        <ul class="doc-list">
          <li>Update the "Effective" date at the top of this Policy;</li>
          <li>Post a prominent notice on the Platform;</li>
          <li>
            Send a notification email to your registered Account address where
            required by law.
          </li>
        </ul>
        <p>
          We encourage you to review this Policy periodically. Continued use of
          the Platform after changes are posted constitutes acceptance of the
          updated Policy.
        </p>
      </section>

      <!-- ── P18: CONTACT & GRIEVANCE ── -->
      <section class="sec" id="p18">
        <span class="sec-num">Section 18</span>
        <h2 class="sec-heading">Contact &amp; Grievance Officer</h2>
        <p>
          Under the DPDPA 2023 and the IT (Reasonable Security Practices and
          Procedures and Sensitive Personal Data or Information) Rules, 2011, we
          have designated a
          <strong>Grievance Officer</strong> to handle privacy-related
          complaints:
        </p>
        <div class="contact-card">
          <div class="contact-item">
            <span class="lbl">Grievance Officer</span>
            Pythrust Technologies OPC Private Limited
          </div>
          <div class="contact-item">
            <span class="lbl">Email</span>
            <a href="mailto:angularize.dev@gmail.com"
              >angularize.dev&#64;gmail.com</a
            >
          </div>
          <div class="contact-item">
            <span class="lbl">Subject Line</span>
            Privacy Grievance — Angularize.dev
          </div>
          <div class="contact-item">
            <span class="lbl">Address</span>
            WeWork, 5th Floor, DLF Two Horizon Centre,<br />
            Harizan Colony, DLF Phase 5, Sector 43,<br />
            Gurugram, Haryana 122002, India
          </div>
          <div class="contact-item">
            <span class="lbl">Response Time</span>
            Within 30 days of receipt
          </div>
        </div>
        <p style="margin-top: 20px">
          If you are not satisfied with our response, you may escalate to the
          <strong>Data Protection Board of India</strong> (once established
          under DPDPA 2023) or to your local Supervisory Authority if you are in
          the EEA.
        </p>
      </section>
    </main>
  </div>
  <!-- /pp-layout -->
</div>
<!-- /pp-wrap -->
